import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const TOOLS = [
  { to: "/calcul-mensualite-credit", label: "Mensualité crédit", icon: "💰" },
  { to: "/calcul-capacite-emprunt", label: "Capacité d'emprunt", icon: "📊" },
  { to: "/estimation-frais-notaire", label: "Frais de notaire", icon: "📋" },
  { to: "/calcul-rentabilite-locative", label: "Rentabilité locative", icon: "🏠" },
  { to: "/calcul-plus-value-immobiliere", label: "Plus-value", icon: "📈" },
  { to: "/generateur-attestation-hebergement", label: "Attestation hébergement", icon: "📄" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const location = useLocation();
  const toolsRef = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setToolsOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <header className="navGlass" style={styles.header}>
      <div style={styles.container}>
        {/* LOGO / BRAND */}
        <Link to="/" style={styles.brand} className="nav-brand">
          <div style={styles.logo}>AT</div>
          <div>
            <div style={styles.brandTitle}>Outils Immobilier</div>
            <div style={styles.brandSub}>simulateurs & PDF</div>
          </div>
        </Link>

        {/* DESKTOP NAV */}
        <nav className="nav-desktop" style={styles.nav} role="navigation" aria-label="Navigation principale">
          <NavLink to="/" end style={navLink} className="nav-link-btn">
            Accueil
          </NavLink>

          <div ref={toolsRef} style={{ position: "relative" }}>
            <button
              onClick={() => setToolsOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setToolsOpen((v) => !v);
                }
              }}
              style={{
                ...styles.navBtn,
                ...(toolsOpen ? styles.navBtnActive : {}),
              }}
              className="nav-link-btn"
              aria-expanded={toolsOpen}
              aria-haspopup="menu"
              aria-label="Menu Outils"
            >
              Outils{" "}
              <span
                style={{
                  display: "inline-block",
                  transition: "transform 0.2s ease",
                  transform: toolsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  marginLeft: 4,
                }}
              >
                ▾
              </span>
            </button>

            <div
              style={{
                ...styles.dropdown,
                opacity: toolsOpen ? 1 : 0,
                transform: toolsOpen ? "translateY(0)" : "translateY(-8px)",
                pointerEvents: toolsOpen ? "auto" : "none",
                transition: "opacity 0.2s ease, transform 0.2s ease",
              }}
              role="menu"
              aria-label="Outils de simulation immobilière"
            >
              {TOOLS.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  style={dropdownLink}
                  className="nav-dropdown-link"
                  role="menuitem"
                >
                  <span style={{ marginRight: 10 }}>{t.icon}</span>
                  {t.label}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink to="/blog" style={navLink} className="nav-link-btn">
            Blog
          </NavLink>

          <NavLink to="/contact" style={navLink} className="nav-link-btn">
            Contact
          </NavLink>
        </nav>

        {/* BURGER */}
        <button
          className="nav-burger"
          onClick={() => setMobileOpen((v) => !v)}
          style={styles.burger}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={mobileOpen}
        >
          <span
            style={{
              ...burgerLine,
              transform: mobileOpen ? "rotate(45deg) translate(4px, 4px)" : "none",
              transition: "transform 0.3s ease",
            }}
          />
          <span
            style={{
              ...burgerLine,
              opacity: mobileOpen ? 0 : 1,
              transition: "opacity 0.2s ease",
            }}
          />
          <span
            style={{
              ...burgerLine,
              transform: mobileOpen ? "rotate(-45deg) translate(4px, -4px)" : "none",
              transition: "transform 0.3s ease",
            }}
          />
        </button>
      </div>

      {/* MOBILE MENU */}
      <div
        style={{
          ...styles.mobilePanel,
          maxHeight: mobileOpen ? "500px" : "0",
          opacity: mobileOpen ? 1 : 0,
          padding: mobileOpen ? "16px" : "0 16px",
          overflow: "hidden",
          transition: "max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease",
        }}
        role="menu"
        aria-label="Menu mobile"
      >
        <NavLink to="/" end style={mobileLink} className="nav-mobile-link" role="menuitem">
          Accueil
        </NavLink>

        <div style={styles.mobileBox}>
          <div style={styles.mobileTitle}>Outils</div>
          {TOOLS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              style={mobileSubLink}
              className="nav-mobile-link"
              role="menuitem"
            >
              <span style={{ marginRight: 10 }}>{t.icon}</span>
              {t.label}
            </NavLink>
          ))}
        </div>

        <NavLink to="/blog" style={mobileLink} className="nav-mobile-link" role="menuitem">
          Blog
        </NavLink>

        <NavLink to="/contact" style={mobileLink} className="nav-mobile-link" role="menuitem">
          Contact
        </NavLink>
      </div>

      {/* STYLES */}
      <style>{`
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-burger { display: flex !important; }
        }
        @media (min-width: 901px) {
          .nav-burger { display: none !important; }
        }

        .nav-link-btn {
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
        }
        .nav-link-btn:hover {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
        }

        .nav-link-btn:focus-visible,
        .nav-dropdown-link:focus-visible,
        .nav-mobile-link:focus-visible,
        .nav-burger:focus-visible {
          outline: 2px solid #1e40af;
          outline-offset: 2px;
        }

        .nav-dropdown-link {
          transition: background 0.15s ease !important;
        }
        .nav-dropdown-link:hover {
          background: #f1f5f9 !important;
        }

        .nav-mobile-link {
          transition: background 0.15s ease !important;
        }
        .nav-mobile-link:hover {
          background: #f1f5f9 !important;
        }

        .nav-brand:hover {
          opacity: 0.9;
        }
      `}</style>
    </header>
  );
}

/* ===== Styles - Thème Clair Pro ===== */

const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textDecoration: "none",
    color: "#1e293b",
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: 16,
    color: "white",
    background: "linear-gradient(135deg, #1e40af, #3b82f6)",
    boxShadow: "0 2px 8px rgba(30, 64, 175, 0.3)",
  },
  brandTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: "#1e293b",
  },
  brandSub: {
    fontSize: 12,
    color: "#64748b",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  navBtn: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    background: "white",
    color: "#475569",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: 14,
  },
  navBtnActive: {
    background: "#f1f5f9",
    borderColor: "#cbd5e1",
  },
  dropdown: {
    position: "absolute",
    top: 48,
    right: 0,
    width: 280,
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 8,
    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
  },
  burger: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 5,
    cursor: "pointer",
  },
  mobilePanel: {
    display: "grid",
    gap: 8,
    borderTop: "1px solid #e2e8f0",
    background: "white",
  },
  mobileBox: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#f8fafc",
  },
  mobileTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: "#64748b",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
};

const navLink = ({ isActive }) => ({
  padding: "10px 16px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: isActive ? "#1e40af" : "white",
  textDecoration: "none",
  color: isActive ? "white" : "#475569",
  fontWeight: 500,
  fontSize: 14,
});

const dropdownLink = ({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  padding: "12px 14px",
  borderRadius: 8,
  textDecoration: "none",
  color: isActive ? "#1e40af" : "#1e293b",
  fontWeight: isActive ? 600 : 500,
  fontSize: 14,
  background: isActive ? "#eff6ff" : "transparent",
});

const mobileLink = ({ isActive }) => ({
  display: "block",
  padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: isActive ? "#1e40af" : "white",
  textDecoration: "none",
  color: isActive ? "white" : "#475569",
  fontWeight: 500,
  fontSize: 14,
});

const mobileSubLink = ({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: 8,
  textDecoration: "none",
  color: isActive ? "#1e40af" : "#1e293b",
  fontWeight: isActive ? 600 : 500,
  fontSize: 14,
  background: isActive ? "#eff6ff" : "transparent",
});

const burgerLine = {
  width: 20,
  height: 2,
  background: "#475569",
  borderRadius: 2,
};
